import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components';
import Background from '../components/Background';
import logger from '../utils/logger';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../contexts/ProfileContext';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile, quote, isLoading } = useProfile();
  const [day, setDay] = useState<'today' | 'tomorrow'>('today');
  const [asking, setAsking] = useState(false);

  const username = profile?.user?.name || user?.email?.split('@')[0] || 'Traveler';

  // Debug logging
  useEffect(() => {
    logger.debug('Home', 'Profile context state', {
      hasProfile: !!profile,
      hasQuote: !!quote,
      isLoading,
      username,
      profileName: profile?.user?.name,
      userEmail: user?.email
    });
  }, [profile, quote, isLoading, username, user]);

  const handleDeepDive = () => {
    logger.userAction('Home', 'Navigate to deep dive reading', { day });
    const questionText = day === 'today'
      ? 'How is my day today?'
      : 'How is my day tomorrow?';
    navigate(`/reading?type=daily&day=${day}&autofill=true&question=${encodeURIComponent(questionText)}`);
  };

  const handleCustomQuestion = () => {
    if (asking) return;
    setAsking(true);
    // Navigate to reading page with autofill flag - user will type in the prompt
    navigate(`/reading?type=custom&autofill=true`);
    setAsking(false);
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col" style={{ backgroundColor: '#050508' }}>
      <Background />

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-12 max-w-3xl mx-auto w-full">

        {/* Greeting */}
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading text-white tracking-luxury mb-6" style={{
            textShadow: '0 0 40px rgba(255, 255, 255, 0.1)'
          }}>
            Greetings, {username}
          </h1>

          {quote && (
            <div className="max-w-lg mx-auto mt-8 relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cosmic-gold/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" />
              <div
                className="relative glass-grain rounded-xl px-8 py-6 backdrop-blur-xl border transition-all duration-500"
                style={{
                  backgroundColor: 'rgba(20, 20, 32, 0.2)',
                  borderColor: 'rgba(255, 184, 79, 0.15)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                }}
              >
                <p className="text-text-secondary font-elegant text-lg sm:text-xl italic text-center leading-relaxed">
                  "{quote}"
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Daily Check-in */}
        <div className="w-full max-w-md animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="text-center mb-8">
            <h2 className="text-2xl font-heading text-white/90 tracking-wide mb-6">
              Your Daily Alignment
            </h2>

            {/* Toggle Switch */}
            <div className="inline-flex bg-white/5 rounded-full p-1 border border-white/10 backdrop-blur-md relative">
              <div
                className="absolute inset-y-1 rounded-full bg-cosmic-gold/20 transition-all duration-300 ease-out"
                style={{
                  left: day === 'today' ? '4px' : '50%',
                  width: 'calc(50% - 4px)',
                  boxShadow: '0 0 20px rgba(255, 184, 79, 0.2)'
                }}
              />
              <button
                onClick={() => setDay('today')}
                className={`relative z-10 px-8 py-2 rounded-full font-elegant text-lg transition-colors duration-300 ${day === 'today' ? 'text-white' : 'text-white/50 hover:text-white/80'
                  }`}
              >
                Today
              </button>
              <button
                onClick={() => setDay('tomorrow')}
                className={`relative z-10 px-8 py-2 rounded-full font-elegant text-lg transition-colors duration-300 ${day === 'tomorrow' ? 'text-white' : 'text-white/50 hover:text-white/80'
                  }`}
              >
                Tomorrow
              </button>
            </div>
          </div>

          {/* Main Action */}
          <div className="space-y-6">
            <button
              onClick={handleDeepDive}
              className="w-full group relative overflow-hidden rounded-2xl p-[1px] transition-all duration-300 hover:scale-[1.02]"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cosmic-gold/0 via-cosmic-gold/50 to-cosmic-gold/0 opacity-50 group-hover:opacity-100 transition-opacity duration-500 animate-shimmer" />
              <div className="relative bg-[#0A0A0F] rounded-2xl px-8 py-5 flex items-center justify-center gap-3 group-hover:bg-[#141420] transition-colors duration-300">
                <span className="font-heading text-xl text-white tracking-wide">
                  Unveil {day === 'today' ? "Today's" : "Tomorrow's"} Guidance
                </span>
                <span className="text-cosmic-gold text-xl group-hover:translate-x-1 transition-transform duration-300">→</span>
              </div>
            </button>

            <button
              onClick={handleCustomQuestion}
              className="w-full text-center py-3"
            >
              <span className="font-elegant text-lg text-white/40 hover:text-cosmic-gold/80 transition-colors duration-300 border-b border-transparent hover:border-cosmic-gold/30 pb-1">
                Seek Wisdom on Another Topic
              </span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Home;


